import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthEndpoint, Auth, User, MeEndpoint } from '@fullyops/legacy/data';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, filter, map } from 'rxjs/operators';
import {
  PermissionType,
  PermissionsType,
  RoleGroup,
} from '../ui-shared/utils/crm-types';
import { CookieService } from 'ngx-cookie-service';
import { TenantResolverService } from '@fullyops/shared/services/tenant/tenant-resolver.service';
import { Router } from '@angular/router';
import { LocaleService } from '@fullyops/shared/services/locale/locale.service';

type AuthenticatedUser = User | null;

@Injectable({
  providedIn: 'root',
})
export class UiAuthService {
  currentUser$ = new BehaviorSubject<User | null>(null);

  constructor(
    protected auth: AuthEndpoint,
    private me: MeEndpoint,
    private cookieService: CookieService,
    private tenantResolverService: TenantResolverService,
    protected router: Router,
    private localeService: LocaleService,
  ) {
    if (this.auth.$authorazied.getValue()) {
      this.fetchUser();
    }
  }

  public get currentUser(): AuthenticatedUser {
    return this.currentUser$.getValue();
  }

  public get authorizedObs() {
    return this.auth.$authorazied;
  }

  hasRole(requestedRole: RoleGroup): boolean {
    return !!this.currentUser.roles.filter((role) => requestedRole == role.name)
      .length;
  }

  /**
   * @deprecated Prefer {@link hasPermissionNoAsync}. Permissions are fixed for
   * the lifetime of a session, so this observable never emits a second value
   * and only forces callers into a subscription they then have to tear down.
   * The `ownerId` argument has no synchronous counterpart, but no caller uses
   * it.
   */
  hasPermission(
    requestedPermissions: readonly PermissionType[],
    ownerId?: string,
  ): Observable<boolean> {
    const userPermissions$ = this.currentUser$.pipe(
      filter((user) => user !== null),
      map((user) => {
        const userPermissions = new Set(
          user.roles.flatMap((role) =>
            role.permissions.map((perm) => perm.name),
          ),
        );

        return userPermissions as Set<PermissionType>;
      }),
    );

    return userPermissions$.pipe(
      map((permissions) => {
        const validPermissions = requestedPermissions.filter((e) =>
          permissions.has(e),
        );

        //Will only check the ownerId if we have it. If not, it will consider a permission before having the ticket/entity
        if (validPermissions.length == 1 && ownerId) {
          // Only permission valid is OWN
          if (validPermissions[0].includes('_OWN_')) {
            return this.currentUser$.value?.id == ownerId;
          }
          return true;
        } else {
          if (validPermissions.length > 0) return true;
          else return false;
        }
      }),
    );
  }

  hasPermissionNoAsync(
    requestedPermissions: readonly (PermissionType | PermissionsType)[],
  ) {
    const permissionsByRole = this.currentUser$.value.roles.map((role) =>
      role.permissions.map((permission) => permission.name),
    );

    const allPermissions: PermissionsType[] = Array.prototype.concat.apply(
      [],
      permissionsByRole,
    );

    return allPermissions.some((permission) =>
      requestedPermissions.includes(permission),
    );
  }

  login(email: string, password: string): Observable<{}> {
    const tenantName = this.tenantResolverService.getActiveTenant();
    return this.auth.login(email, password, tenantName).pipe(
      tap({
        next: (res) => {
          if (res instanceof HttpErrorResponse) return;
          return this.fetchUser();
        },
      }),
    );
  }

  fetchUser(pageRefresh: boolean = false) {
    this.me
      .get()
      .pipe(
        filter((response) => response.data !== null),
        map((response) => response.data as User),
      )
      .subscribe((user) => {
        this.currentUser$.next(user);
        this.setApplicationLanguage(pageRefresh);
      });
  }

  refreshToken() {
    this.auth
      .refreshToken()
      .pipe(
        tap((response: Auth) => {
          if (response.jwt !== '') {
            this.me
              .get()
              .pipe(
                filter((r) => r.data !== null),
                map((r) => r.data as User),
              )
              .subscribe(this.currentUser$);

            this.auth.setAuthorize(true);
          } else {
            this.currentUser$.next(null);
          }
        }),
        catchError((error) => {
          this.currentUser$.next(null);
          return of(error);
        }),
      )
      .subscribe();
  }

  logout() {
    this.auth
      .logout()
      .pipe(
        tap((_) => {
          this.auth.setAuthorize(false);
          this.currentUser$.next(null);
        }),
      )
      .subscribe();
  }

  getPageFilters(page: string) {
    const filterKey = this.currentUser.id + page;
    return JSON.parse(localStorage.getItem(filterKey)) ?? {};
  }

  setPageFilters(page: string, filters: {}) {
    const filterKey = this.currentUser.id + page;
    localStorage.setItem(filterKey, JSON.stringify(filters));
  }

  setApplicationLanguage(pageRefresh: boolean) {
    const userLocale = this.currentUser$.value.locale;

    if (userLocale !== this.localeService.getCurrentLanguage()) {
      this.localeService.setLanguage(userLocale).then(() => {
        const routeChange = this.router.navigate([this.router.url]);
        // angular-18next does not recognize a language change if the page isn't reloaded
        // TODO: Implement OnPush change detection strategy and use the i18nextEager pipe.
        // TODO: See https://www.npmjs.com/package/angular-i18next
        if (pageRefresh) {
          routeChange.then(() => window.location.reload());
        }
      });
    }
  }
}
